import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useCatalogContext } from '@magento/peregrine/lib/context/catalog';
import { useCartContext } from '@overrides_venia/peregrine/context/cart';
import { useCheckoutContext } from '@magento/peregrine/lib/context/checkout';
var MenuTypeConflictContext = createContext();
export var MenuTypeConflictProvider = _ref => {
  var {
    children
  } = _ref;
  var [pendingAddToCart, setPendingAddToCart] = useState(null);
  var [conflictModalOpen, setConflictModalOpenInternal] = useState(false);
  var [{
    products
  }] = useCatalogContext();
  var [{
    details
  }] = useCartContext();
  var [{
    tableData
  }] = useCheckoutContext();

  // Wrapper to log state changes
  var setConflictModalOpen = useCallback(value => {
    console.log('[MenuTypeConflict] setConflictModalOpen called', {
      from: conflictModalOpen,
      to: value,
      pendingProduct: (pendingAddToCart === null || pendingAddToCart === void 0 ? void 0 : pendingAddToCart.productSku) || null
    });
    setConflictModalOpenInternal(value);
  }, [conflictModalOpen, pendingAddToCart]);

  // Check if adding a product would create a menu type conflict
  var wouldCreateMenuTypeConflict = useCallback(newProductSku => {
    var _details$items;
    // Only check for conflicts if sales type is CASUALDINING
    if ((tableData === null || tableData === void 0 ? void 0 : tableData.sales_type) !== "CASUALDINING") return false;
    var newProduct = products.find(p => p.sku === newProductSku);
    if (!newProduct || !newProduct.menu_type) return false;
    var cartProductSKUs = (details === null || details === void 0 ? void 0 : (_details$items = details.items) === null || _details$items === void 0 ? void 0 : _details$items.map(item => item.product.sku)) || [];
    var cartProducts = products.filter(product => cartProductSKUs.includes(product.sku));
    var currentMenuTypes = (cartProducts === null || cartProducts === void 0 ? void 0 : cartProducts.map(p => p.menu_type)) || [];
    var newMenuType = newProduct.menu_type;

    // Check if adding this product would create a conflict
    var allMenuTypes = [...currentMenuTypes, newMenuType];
    return (allMenuTypes.includes("1") || allMenuTypes.includes("2")) && (allMenuTypes.includes("D") || allMenuTypes.includes("C"));
  }, [products, details, tableData]);

  // Intercept add to cart and check for conflicts
  var interceptAddToCart = useCallback((originalAddToCart, productSku) => {
    if (wouldCreateMenuTypeConflict(productSku)) {
      setPendingAddToCart({
        addToCartFunction: originalAddToCart,
        productSku: productSku
      });
      setConflictModalOpen(true);
      return Promise.resolve(); // Return resolved promise to prevent original function from running
    } else {
      return originalAddToCart();
    }
  }, [wouldCreateMenuTypeConflict]);

  // Confirm adding the conflicting product
  var confirmAddConflictingProduct = useCallback( /*#__PURE__*/_asyncToGenerator(function* () {
    console.log('[MenuTypeConflict] confirmAddConflictingProduct called', {
      pendingAddToCart
    });
    if (pendingAddToCart) {
      yield pendingAddToCart.addToCartFunction();
      setPendingAddToCart(null);
    }
    setConflictModalOpen(false);
  }), [pendingAddToCart, setConflictModalOpen]);

  // Cancel adding the conflicting product
  var cancelAddConflictingProduct = useCallback(() => {
    setPendingAddToCart(null);
    setConflictModalOpen(false);
  }, []);

  // Reset menu type conflict state (called after order is placed)
  var resetMenuTypeConflictState = useCallback(() => {
    setPendingAddToCart(null);
    setConflictModalOpen(false);
  }, []);
  var contextValue = {
    interceptAddToCart,
    confirmAddConflictingProduct,
    cancelAddConflictingProduct,
    resetMenuTypeConflictState,
    pendingProduct: (pendingAddToCart === null || pendingAddToCart === void 0 ? void 0 : pendingAddToCart.productSku) || null,
    conflictModalOpen,
    setConflictModalOpen
  };
  return /*#__PURE__*/React.createElement(MenuTypeConflictContext.Provider, {
    value: contextValue
  }, children);
};
export var useMenuTypeConflict = () => {
  var context = useContext(MenuTypeConflictContext);
  if (!context) {
    throw new Error('useMenuTypeConflict must be used within a MenuTypeConflictProvider');
  }
  return context;
};